import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Button, Checkbox, Pagination, App, Empty, Modal, Dropdown, Menu, BackTop } from 'antd';
import { DownOutlined, MenuOutlined, SearchOutlined, PlusOutlined, ExportOutlined, DeleteOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearYourTownloopSearchValue } from '@/store/slices/searchBarSlice';
import { eventBus } from '@/utils/eventBus';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProductListStore, deleteProduct } from '@/api/products';
import styles from './YourProducts.module.scss';
import ServiceCategory from './ServiceCategory';
import { useTranslation } from 'react-i18next';


// 标签项
const tabItems = [
  { label: 'All', key: 'all' },
  { label: 'For Sale', key: 'forSale' },
  { label: 'Stocked', key: 'stocked' },
  { label: 'Draft', key: 'draft' },
  { label: 'Trash', key: 'trash' },
  { label: 'Violation', key: 'violation' },
  { label: 'Obsolete Delisting', key: 'obsolete' },
];

    
// 产品类型定义
interface Product { 
  product_id: string;
  product_title: string;
  product_content?: string;
  product_status: number;
  create_time: string;
  update_time: string;
  product_category: {
    product_category_id: number;
    product_category_name: string;
  };
  product_category_brand: {
    product_category_brand_id: number;
    brand_name: string;
  };
  product_spu: {
    product_spu_id: number;
    product_spu_name: string;
  };
  // 修正为实际接口结构
  product_sku_values: Array<{
    product_sku_id: string;
    product_price: string;
    product_stock: number;
    is_on_shelf: boolean;
    // 其他字段可按需补充
  }>;
  product_media_list: Array<{
    product_media_id: number;
    product_media_type: string;
    product_media_file_url: string;
  }>;
}

// 分类类型定义
interface Category {
  id: string;
  name: string;
  icon?: string;
  description?: string;
}

const YourProducts: React.FC = () => {
  //国际化语言切换
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 分类切换状态 - Product Category默认选中
  const [activeCategory, setActiveCategory] = useState('product');
  
  // 标签状态
  const [activeTab, setActiveTab] = useState('all');

  // 搜索关键字
  const [searchValue, setSearchValue] = useState<string>('');

  // 选中的产品
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  // 页面尺寸
  const isMobile = useAppSelector((state) => state.searchBar.isMobile);


  // 发布产品弹窗
  const [publishModalVisible, setPublishModalVisible] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // yourTownloopSearchValue关键字
  const yourTownloopSearchValue = useAppSelector((state) => state.searchBar.yourTownloopSearchValue);

  // 模拟产品分类数据
  // const productCategories: Category[] = [
  //   {
  //     id: '1',
  //     name: 'Product Category',
  //     description: 'Physical products that can be shipped'
  //   },
  //   {
  //     id: '2',
  //     name: 'Service Category',
  //     description: 'Services that can be provided online or offline'
  //   }
  // ];


  const [products, setProducts] = useState<Product[]>([]);

  // 分页信息
  const [goToPage, setGoToPage] = useState<string>('');

  // 分页参数类型定义
  interface PaginationParams {
    page_num: number;
    page_size: number;
    total: number;
    totalPages: number;
  }

  // 分页参数
  const [params, setParams] = useState<PaginationParams>({
    page_num: 1,
    page_size: 10,
    total: 0,
    totalPages: 1
  });

  const fetchProductList = async (pageNum: number, pageSize: number, keyword?: string, updateParams: boolean = true) => {
    try {
      const res = await getProductListStore({
        page_num: pageNum,
        page_size: pageSize,
        keyword: keyword || '',
      });
      
      console.log('fetchProductList res', res);
      
      if (res.message === "Success") {
        setProducts(res.data || []);
        
        if (updateParams) {
          setParams(prevParams => ({
            ...prevParams,
            page_num: pageNum,
            page_size: pageSize,
            total: res.total || 0,
            totalPages: res.totalPages || 1,
          }));
        }
        
        return res;
      } 
    } catch (error) {

    }
  };

  //获取商品分页列表
  const getProductListData = () => {
    fetchProductList(params.page_num, params.page_size, searchValue);
  }

  // 页面初始化
  useEffect(() => {
    getProductListData();
  }, []);

  // 监听URL自动切换分类
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl === 'service') {
      setActiveCategory('service');
    }
  }, [searchParams]);

  // 监听params变化，重新获取数据
  useEffect(() => {
    if (params.page_num > 0 && params.page_num !== 1) {
      getProductListData();
    }
  }, [params.page_num, params.page_size]);

  // 监听当前页码变化，更新go to page输入框
  useEffect(() => {
    setGoToPage(params.page_num.toString());
  }, [params.page_num]);

  // 切换分类
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setActiveTab('all');
    setSelectedProducts([]);
    setParams({
      ...params,
      page_num: 1,
    });
  };

  // 切换标签
  const changeTab = (key: string) => {
    console.log('key', key);
    setActiveTab(key);
    setSelectedProducts([]);
    setParams({
      ...params,
      page_num: 1,
    });
  };

  // 搜索操作
  const searchAction = () => {
    console.log('searchValue', searchValue);
    fetchProductList(1, params.page_size, searchValue);
  };

  // 搜索处理键盘回车事件
  const searchHandleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchAction();
    }
  };

  // 重置搜索
  const resetSearch = () => {
    setSearchValue('');
    fetchProductList(1, params.page_size, '');
  };

  // 全选/取消全选
  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      setSelectedProducts(products.map(product => product.product_id));
    } else {
      setSelectedProducts([]);
    }
  };

  // 选择单个产品
  const handleSelectProduct = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

    // 批量删除
  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) {
      message.warning('Please select products to delete');
      return;
    }
    Modal.confirm({
      title: 'Confirm Bulk Deletion',
      content: `Are you sure you want to delete the selected ${selectedProducts.length} products?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        deleteProduct(selectedProducts)
          .then((res) => {
            if (res.message === "Success") {
              message.success(`Successfully deleted ${selectedProducts.length} products`);
              setSelectedProducts([]);
              getProductListData(); // 重新获取产品列表
            } else {
            }
          })
          .catch((error) => {
          });
      },
    });
  };

  // 发布产品
  const handlePublishProduct = () => {
    // setPublishModalVisible(true);
    // setSelectedCategory('');
    navigate('/StorePublishProduct');
  };

  // 选择产品分类
  // const handleSelectCategory = (categoryId: string) => {
  //   setSelectedCategory(categoryId);
  //   // message.success(`选择了分类ID: ${categoryId}`);
  //   if (categoryId == '1') {
  //     navigate('/StorePublishProduct');
  //   } else {
  //     navigate('/serviceCategory');
  //   }
  //   // 这里可以根据选择的分类跳转到不同的产品发布页面

  // };

  // // 关闭发布产品弹窗
  // const handleClosePublishModal = () => {
  //   setPublishModalVisible(false);
  // };


  // 导出所有数据
  const handleExport = async () => {
    if (params.total === 0) {
      message.warning('No data available for export');
      return;
    }
    try {
      const allProducts: Product[] = [];
      const totalPages = Math.ceil(params.total / params.page_size);
      // 分页获取所有数据
      for (let page = 1; page <= totalPages; page++) {
        const res = await fetchProductList(page, params.page_size, searchValue, false);
        
        if (res && res.data) {
          allProducts.push(...res.data);
        }
      }
      
      message.destroy();
      
      if (allProducts.length > 0) {
        exportToCSV(allProducts, `products_all_${new Date().toISOString().split('T')[0]}.csv`);
      } else {
      }
    } catch (error) {
      message.destroy();

    }
  };

  // 通用CSV导出函数
  const exportToCSV = (data: Product[], filename: string) => {
    // 准备CSV数据
    const csvData = data.map(product => ({
      'Product ID': product.product_id,
      'Product Title': product.product_title,
      'Category': product.product_category?.product_category_name || '',
      'Brand': product.product_category_brand?.brand_name || '',
      'SPU': product.product_spu?.product_spu_name || '',
        'Price': product.product_sku_values && product.product_sku_values.length > 0 
          ? `$${product.product_sku_values?.find((sku: any) => sku.product_price !== "0.00")?.product_price || product.product_sku_values[0]?.product_price || 0}` 
          : 0.00,
      'Stock': product.product_sku_values && product.product_sku_values.length > 0 
        ? product.product_sku_values[0]?.product_stock 
        : 0,
      'Status': product.product_status,
      'Create Time': new Date(product.create_time).toLocaleString(),
      'Update Time': new Date(product.update_time).toLocaleString(),
    }));

    // 转换为CSV格式
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = (row as any)[header];
          // 如果值包含逗号、引号或换行符，需要用引号包围
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // 创建并下载文件
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };



  // 编辑产品
  const handleEdit = (id: string) => {
    navigate(`/StorePublishProductEdit?id=${id}`);
  };

  const handleShowcase = (id: string) => {
    navigate(`/StorePublishProductShowcase?id=${id}`);
  };

  // 删除产品
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this product?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        deleteProduct([id]).then((res) => {
          if (res.message === "Success") {
            message.success('Product deleted successfully');
            setSelectedProducts(selectedProducts.filter(item => item !== id));
            getProductListData(); // 重新获取产品列表
          } else {
            message.error('Failed to delete product');
          }
        }).catch((error) => {
          console.error('Failed to delete product:', error);
          message.error('Failed to delete product');
        });
      },
    });
  };

  // 分页变化
  const paginationChange = (page: number, pageSize: number) => {
    console.log('page', page, pageSize);
    fetchProductList(page, pageSize, searchValue);
  };

  // 跳转到指定页
  const handleGoToPage = () => {
    const page = parseInt(goToPage);
    if (isNaN(page) || page < 1) {
      message.error('Please enter a valid page number');
      return;
    }

    // if (page > params.totalPages) {
    //   message.error(`Maximum page number is ${params.totalPages}`);
    //   return;
    // }

    fetchProductList(page, params.page_size, searchValue).then(() => {
      setGoToPage('');
    });
  };

  // 处理页码输入框回车事件
  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // mounted 和unmounted
  useEffect(() => {
    return () => {
      console.log('组件销毁时');
      // 销毁清除townloopSearchValue的值
      eventBus.emit('deleteTownloopSearchValue');
      dispatch(clearYourTownloopSearchValue());
    }
  }, [dispatch]);

  // yourTownloopSearchValue关键字的监听
  useEffect(() => {
    console.log('YourProducts组件yourTownloopSearchValue', yourTownloopSearchValue);
    if (yourTownloopSearchValue) {
      setSearchValue(yourTownloopSearchValue);
      searchAction();
    }
  }, [yourTownloopSearchValue]);

  // 渲染产品列表
  const renderProductList = () => {
    if (products.length === 0) {
      return (
        <Empty
          description="No product data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '40px 0' }}
        />
      );
    }

    return (
      <div className={styles['products-table']}>
        <div className={styles['table-header']}>
          <div className={styles['checkbox-column']}>
            <Checkbox
              checked={selectedProducts.length === products.length && products.length > 0}
              indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
              onChange={handleSelectAll}
            />
          </div>
          <div className={styles['product-column']}>{t('storeInformation.ProductName')}</div>
          <div className={styles['price-column']}>{t('storeInformation.Price')}</div>
          <div className={styles['inventory-column']}>{t('storeInformation.Inventory')}</div>
          <div className={styles['time-column']}>{t('storeInformation.CreationTime')}</div>
          <div className={styles['operations-column']}>{t('storeInformation.Operations')}</div>
        </div>

        {products.map(product => (
          <div className={styles['table-row']} key={product.product_id}>
            <div className={styles['checkbox-column']}>
              <Checkbox
                checked={selectedProducts.includes(product.product_id)}
                onChange={() => handleSelectProduct(product.product_id)}
              />
            </div>
            <div className={styles['product-column']}>
              <div className={styles['product-info']}>
                <img
                  onClick={()=>navigate(`/ProductDetail/${product.product_id}`)}
                  className={styles['product-image']}
                  src={product.product_media_list && product.product_media_list.length > 0
                    ? product.product_media_list[0]?.product_media_file_url
                    : 'https://via.placeholder.com/60x60?text=No+Image'
                  }
                  alt={product.product_title}
                />
                <div className={styles['product-details']}>
                  <div className={styles['product-name']} title={product.product_title}>{product.product_title}</div>
                  <div className={styles['product-classification']}>{product.product_category.product_category_name}</div>
                </div>
              </div>
            </div>
            <div className={styles['price-column']}>
              {product.product_sku_values && product.product_sku_values.length > 0
                ? `$${product.product_sku_values?.find((sku: any) => sku.product_price !== "0.00")?.product_price || product.product_sku_values[0]?.product_price || 0}`
                : 0.00
              }
            </div>
            <div className={styles['inventory-column']}>
              {/* 这个库存也是 */}
              {product.product_sku_values && product.product_sku_values.length > 0
                ? product.product_sku_values?.find((sku: any) => sku.product_price !== 0)?.product_stock || product.product_sku_values[0]?.product_stock || 0
                : 0
              }
            </div>
            <div className={styles['time-column']}>
              {new Date(product.create_time).toLocaleString()}
            </div>
            <div className={styles['operations-column']}>
              <Button
                className={styles['edit-button']}
                onClick={() => handleEdit(product.product_id)}
                icon={isMobile ? <EditOutlined /> : null}
              >
                {!isMobile && t('storeInformation.Edit')}
              </Button>
              <Button
                className={styles['edit-button']}
                onClick={() => handleShowcase(product.product_id)}
                icon={isMobile ? <EditOutlined /> : null}
              >
                {!isMobile && t('storeInformation.Showcase')}
              </Button>
              <Button
                className={styles['delete-button']}
                onClick={() => handleDelete(product.product_id)}
                icon={isMobile ? <DeleteOutlined /> : null}
              >
                {!isMobile && t('storeInformation.Delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  // 渲染发布产品弹窗
  // const renderPublishModal = () => {
  //   return (
  //     <Modal
  //       open={publishModalVisible}
  //       footer={null}
  //       closable={false}
  //       centered
  //       width={600}
  //       className={styles['publish-modal']}
  //       maskClosable={false}
  //     >
  //       <div className={styles['modal-content']}>
  //         <div className={styles['modal-header']}>
  //           <div className={styles['modal-close']} onClick={handleClosePublishModal}>
  //             <CloseOutlined />
  //           </div>
  //         </div>
  //      <div className={styles['category-container']}>
  //           {productCategories.map((category) => (
  //             <div
  //               key={category.id}
  //               className={`${styles['category-card']} ${selectedCategory === category.id ? styles['selected'] : ''}`}
  //               onClick={() => handleSelectCategory(category.id)}
  //             >
  //               <div className={styles['category-icon']}>
  //                 {category.id === '1' ? (
  //                   <div className={styles['product-icon']}></div>
  //                 ) : (
  //                   <div className={styles['service-icon']}></div>
  //                 )}
  //               </div>
  //               <div className={styles['category-name']}>{category.name}</div>
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     </Modal>
  //   );
  // };
  return (
    <div className={styles['your-products-box']}>
      {/* 分类切换table */}
      <div className={styles['category-switch-container']}>
        <div className={styles['category-switch']}>
          <div 
            className={`${styles['category-tab']} ${activeCategory === 'product' ? styles['active'] : ''}`}
            onClick={() => handleCategoryChange('product')}
          >
            {t('storeInformation.ProductCategory')}
          </div>
          <div 
            className={`${styles['category-tab']} ${activeCategory === 'service' ? styles['active'] : ''}`}
            onClick={() => handleCategoryChange('service')}
          >
            {t('storeInformation.ServiceCategory')}
          </div>
        </div>
      </div>

      {/* 根据分类显示不同内容 */}
      {activeCategory === 'service' ? (
        <ServiceCategory />
      ) : (
        <>
          {/* <div className={styles['tabs-container']}>
            <Tabs
              className={styles['products-tabs']}
              items={tabItems}
              activeKey={activeTab}
              onChange={changeTab}
            />
          </div> */}

      <div className={styles['search-container']}>
        <div className={styles['search-input-wrapper']}>
          <input
            className={styles['search-input']}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={searchHandleKeyDown}
            type="text"
            placeholder={t('storeInformation.SearchProduct')}
          />
          <Button
            className={styles['search-button']}
            onClick={searchAction}
            icon={isMobile ? <SearchOutlined /> : null}
          >
            {isMobile ? '' : t('storeInformation.Search')}
          </Button>
          <Button
            className={styles['reset-button']}
            onClick={resetSearch}
          >
            {t('storeInformation.Reset')}
          </Button>
        </div>
      </div>

      <div className={styles['action-buttons']}>
        <Button
          className={styles['publish-button']}
          onClick={handlePublishProduct}
          icon={<PlusOutlined />}
        >
          {t('storeInformation.PublishProduct')}
        </Button>
        <Button
          className={styles['export-button']}
          onClick={handleExport}
          icon={<ExportOutlined />}
        >
          {t('storeInformation.ExportAll')}
        </Button>
        <Button
          className={styles['delete-button']}
          onClick={handleBulkDelete}
          icon={<DeleteOutlined />}
          disabled={selectedProducts.length === 0}
        >
          {t('storeInformation.BulkDelete')}
        </Button>
        <div className={styles['total-count']}>{t('storeInformation.Total')} {params.total} {t('storeInformation.Products')}</div>
      </div>

      {renderProductList()}

      <div className={styles['pagination-container']}>
        <div className={styles['pagination-info']}>{t('storeInformation.Total')} {params.total} {t('storeInformation.Products')}</div>
        <Pagination
          onChange={paginationChange}
          current={params.page_num}
          pageSize={params.page_size}
          total={params.total}
          showSizeChanger={false}
          responsive
          hideOnSinglePage={params.totalPages <= 1}
        />
        <div className={styles['go-to-page']}>
        {t('storeInformation.goto')}
          <input
            className={styles['page-input']}
            type="text"
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={handlePageInputKeyDown}
          />
           {t('storeInformation.page')}
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={handleGoToPage}
          >
            {t('storeInformation.Go')}
          </Button>
        </div>
      </div>

          {/* {renderPublishModal()} */}
        </>
      )}
    </div>
  );
};

export default YourProducts; 
